import React from 'react'

import { useTheme } from '@emotion/react'
import { Trans, useTranslation } from 'react-i18next'

import { ArcadeLink, Hug, pxToRem, Stack, Text } from '@cash-design-system/react'
import { usePreferredLocale } from '@cash-web/shared-feature-locale-preference'
import { regionConfig } from '@cash-web/shared-region-feature-main'
import { Region } from '@cash-web/shared-region-ui-context-main'

import { Alias } from './BlockerAlias'

const Link = ({ children, link, ariaLabel }: React.PropsWithChildren<{ link: string; ariaLabel: string }>) => (
  <ArcadeLink href={link} target="_blank" rel="noreferrer" ariaLabel={ariaLabel} typography="linkSmall">
    {children}
  </ArcadeLink>
)

/* eslint-disable @nx/workspace-i18n-no-hardcoded-strings, @nx/workspace-i18n-no-hardcoded-aria-labels, i18next/no-literal-string */
const SpanishDisclaimer = () => (
  <Text typography="bodySmall" color="textSubtle">
    La versión en español de Cash App se ofrece como cortesía, y algunos contenidos solo están disponibles en inglés.
    Consulta los{' '}
    <Link
      link="https://cash.app/legal/cash-en-espanol-general-terms-conditions"
      ariaLabel="Términos y condiciones en español"
    >
      Términos y condiciones en español
    </Link>
    . Para usar Cash App en inglés, actualiza los ajustes de idioma de tu dispositivo.
  </Text>
)
/* eslint-enable @nx/workspace-i18n-no-hardcoded-strings, @nx/workspace-i18n-no-hardcoded-aria-labels, i18next/no-literal-string */

export function LegalText({
  region = 'US',
  aliasType,
  isTaxLogin,
}: {
  region: Region
  aliasType: Alias['type']
  isTaxLogin: boolean
}) {
  const theme = useTheme()
  const { t } = useTranslation()
  const preferredLocale = usePreferredLocale()

  const {
    links: { terms, eSignConsent, privacyPolicy },
  } = regionConfig[region]

  const sectionSpacing = pxToRem(14)

  if (isTaxLogin) {
    if (aliasType !== 'phone') {
      return null
    }
    return (
      <Hug maxWidth={theme.maxWidths.content}>
        <Text typography="bodySmall" color="textSubtle">
          {t(
            'By continuing, you agree to receive a one time password confirmation code and informational texts from Cash App. Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP to cancel.',
            { context: 'Legal text for phone input on tax login', legal: true }
          )}
        </Text>
      </Hug>
    )
  }

  return (
    <Hug maxWidth={theme.maxWidths.content}>
      <Stack space={sectionSpacing}>
        {preferredLocale.startsWith('es') && <SpanishDisclaimer />}
        <Text typography="bodySmall" color="textSubtle">
          <Trans
            i18nKey={
              'By continuing, you agree to the <Terms>Terms</Terms>, <EsignConsent>E-Sign Consent</EsignConsent>, & <PrivacyPolicy>Privacy Policy</PrivacyPolicy>.'
            }
            context="Footer text. Links to terms of service, e-sign consent, and privacy policy."
            legal={true}
            components={{
              Terms: (
                <Link
                  link={terms}
                  key={terms}
                  ariaLabel={t('URL link to terms of service.', { context: 'Terms of service link' })}
                />
              ),
              EsignConsent: (
                <Link
                  link={eSignConsent}
                  key={eSignConsent}
                  ariaLabel={t('URL link to e-sign consent.', { context: 'e-sign consent link' })}
                />
              ),
              PrivacyPolicy: (
                <Link
                  link={privacyPolicy}
                  key={privacyPolicy}
                  ariaLabel={t('URL link to privacy policy.', { context: 'Privacy policy link' })}
                />
              ),
            }}
          />
        </Text>
        {aliasType === 'phone' && (
          <Text typography="bodySmall" color="textSubtle">
            {t(
              'By continuing, you also agree to receive a one time password confirmation code and informational texts from Cash App. Message frequency varies. Message and data rates may apply. Reply HELP for help, STOP to cancel.',
              { context: 'Legal text for phone input', legal: true }
            )}
          </Text>
        )}
      </Stack>
    </Hug>
  )
}
